import { styled } from "@mui/system";
import EditIcon from "@mui/icons-material/Edit";
import TimerIcon from '@mui/icons-material/Timer';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom'; 
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, IconButton, Switch, TextField, Box, Chip, Typography, CircularProgress } from "@mui/material";
import TokenContext from './TokenContext';
import Banner from './Banner';
import { useSnackbar } from 'notistack';
import widgetApi from '../api/WidgetApi';
import {
  DAYS_OF_WEEK_MAP,
  STYLED_TABLE_CONFIG,
  STYLED_CELL_CONFIG,
  STYLED_HEADER_CELL_CONFIG
} from '../constants';

import { Grid, Card, CardMedia, CardContent, CardActions, CardActionArea } from '@mui/material';


const StyledTable = styled(Table)(({ theme }) => (STYLED_TABLE_CONFIG));
const StyledCell = styled(TableCell)(({ theme }) => (STYLED_CELL_CONFIG));
const StyledHeaderCell = styled(TableCell)(({ theme }) => (STYLED_HEADER_CELL_CONFIG));


function Banners({ countryCode }) {
  const navigate = useNavigate(); 
  const { token, setToken } = useContext(TokenContext);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedWidget, setSelectedWidget] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const [hoverStates, setHoverStates] = useState({});



  useEffect(() => {
    fetchWidgets(countryCode);
  }, [countryCode]);

  const fetchWidgets = async (countryIsoCode) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const banners = await widgetApi.getBanners(countryIsoCode, token);

      setWidgets(sortBanners(banners));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        enqueueSnackbar('Session expired: Please re-login to continue', { variant: 'info' });
        handleLogout();
      } else {
        enqueueSnackbar('Error fetching banners: ' + error.message, { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('token');
    navigate('/'); 
  };

  const updateBanner = (updatedBanner) => {
    const newBanners = widgets.map(banner =>
      banner.id === updatedBanner.id ? updatedBanner : banner
    );
    setWidgets(newBanners);
  };

  const sortBanners = banners => {
    return banners.sort((a, b) => {
      if (a.enabled === b.enabled) {
        return 0;
      }
      return a.enabled ? -1 : 1;
    });
  }

  const handleOpenBanner = (event, widget) => {
    event.stopPropagation();
    setSelectedWidget(widget);
    setOpen(true);
  };

 
  const handleCloseBanner = () => {
    setSelectedWidget(null);
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const filteredWidgets = widgets.filter((widget) =>
    widget.id.toLowerCase().includes(searchValue.toLowerCase())
    || widget.data[0].additional_information.banner_id.toLowerCase().includes(searchValue.toLowerCase())  
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ backgroundColor: "white", padding: "2rem", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="flex-end" style={{ width: '30%', marginBottom: "1rem" }}>
        <TextField
          fullWidth
          label="Search by ID or campaign ID or more"
          variant="outlined"
          value={searchValue}
          onChange={handleSearchChange}
          style={{ marginBottom: "1rem", maxWidth: "67%",  margin: "auto auto",}}
        />
      </Box>
      <Grid container spacing={3} style={{ maxWidth: "90%", margin: "0 auto", backgroundColor: "#F7F8F9" }}>
        {loading ? (
          <Grid item xs={12} style={{ textAlign: 'center' }}>
            <CircularProgress />
          </Grid>
        ) : (
          filteredWidgets.map((widget) => (
            <Grid item xs={12} sm={8} md={5} lg={2.87} key={widget.id}>
              <Card className="zoomCard"
                style={{
                  transform: hoverStates[widget.id] ? 'scale(1.05)' : 'scale(1.0)',
                  transition: 'transform 0.3s, boxShadow 0.3s, backgroundColor 0.3s',
                  boxShadow: hoverStates[widget.id] ? '0 8px 16px rgba(0,0,0,0.1)' : '0 4px 8px rgba(0,0,0,0.06)',
                  backgroundColor: hoverStates[widget.id] ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 1)',
                }}
                onMouseEnter={() => setHoverStates(prev => ({ ...prev, [widget.id]: true }))}
                onMouseLeave={() => setHoverStates(prev => ({ ...prev, [widget.id]: false }))}
              >
                <CardActionArea onClick={(event) => handleOpenBanner(event, widget)}>
                <CardMedia
                  component="img"
                  alt="invalid image"
                  height="140"
                  image={widget.data[0].image || widget.image}
                  title="Banner Image"
                  onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.src="https://rappi-images-upload-co.s3.amazonaws.com/home-ab-objects/default-1697134364.png"
                  }}
                  style={{
                      borderTopLeftRadius: '15px',
                      borderTopRightRadius: '15px',
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%'
                  }}
              />
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" component="p" align="center">
                      <span style={{ marginRight: '0.5rem' }}>
                        {widget.enabled ? 
                          <><ToggleOnIcon style={{ fontSize: '0.75rem' }} /> Enabled</> : 
                          <><ToggleOffIcon style={{ fontSize: '0.75rem' }} /> Disabled</>
                        }
                      </span>
                      <span style={{ marginRight: '0.5rem' }}>
                        <><TimerIcon style={{ fontSize: '0.75rem' }} /> {widget.param.start_hour}h - {widget.param.end_hour}h</>
                      </span>
                      <span style={{ marginRight: '0.5rem' }}>
                        <><TagFacesIcon style={{ fontSize: '0.75rem' }} /> {widget.param.user_tags.length > 0 ? widget.param.user_tags.length +' tag' : 'All users'}</>
                      </span>
                      <span>
                        <><CalendarTodayIcon style={{ fontSize: '0.75rem' }} /> {(widget.param.days.length > 0 && widget.param.days.length != 7)  ? widget.param.days.length + ' days' : 'All days'}</>
                      </span>
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))
        )}
      </Grid>



      {selectedWidget && (
        <Banner
          open={Boolean(selectedWidget)}
          widget={selectedWidget}
          onUpdate={updateBanner}
          onClose={handleCloseBanner}
          token={token}
          countryCode={countryCode}
        />
      )}
    </Box>
  );
}

export default Banners;